import { Component } from '@angular/core';
import { NgbModal, NgbModalRef, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, Validators, FormBuilder, FormGroup, AbstractControl, ValidationErrors } from '@angular/forms';
import { AuthService } from '../../auth/auth.service';
import { NgbdMessageModal } from '../../modal/message.modal.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { fadeInAnimation } from '../../_animations/fade-in.animation';
import { Arbetstagare } from '../../interfaces/arbetstagare.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionService } from '../../session/session.service';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  animations: [fadeInAnimation]
})
export class SettingsComponent {
  public userForm: FormGroup;
  public isLoaded: boolean;
  private lockedWindow: NgbModalRef;

  public hasChanges: boolean;

  public get personal_number(): AbstractControl { return this.userForm.get('personal_number'); }
  public get first_name(): AbstractControl { return this.userForm.get('first_name'); }
  public get last_name(): AbstractControl { return this.userForm.get('last_name'); }
  public get phone(): AbstractControl { return this.userForm.get('phone'); }
  public get email(): AbstractControl { return this.userForm.get('email'); }
  public get street1(): AbstractControl { return this.userForm.get('street1'); }
  public get street2(): AbstractControl { return this.userForm.get('street2'); }
  public get postal_code(): AbstractControl { return this.userForm.get('postal_code'); }
  public get city(): AbstractControl { return this.userForm.get('city'); }
  public get account_type(): AbstractControl { return this.userForm.get('account_type'); }
  public get account_number(): AbstractControl { return this.userForm.get('account_number'); }
  public get clearing_code(): AbstractControl { return this.userForm.get('clearing_code'); }
  public get iban_number(): AbstractControl { return this.userForm.get('iban_number'); }
  public get bic_code(): AbstractControl { return this.userForm.get('bic_code'); }
  public get message_text(): AbstractControl { return this.userForm.get('message_text'); }

  constructor(
    protected auth: AuthService,
    protected modalService: NgbModal,
    protected sessionService: SessionService,
    protected fb: FormBuilder,
    protected spinner: NgxSpinnerService,
    protected route: ActivatedRoute,
    protected router: Router) { }

  private customEmailValidator(control: AbstractControl): ValidationErrors {
    if (!control.value) {
      return null;
    }

    return Validators.email(control);
  }

  private ngOnInit(): void {
    this.userForm = this.fb.group({
      'personal_number': new FormControl({ value: '', disabled: true }, [
        Validators.required]),
      'first_name': new FormControl('', [
        Validators.required]),
      'last_name': new FormControl('', [
        Validators.required]),
      'phone': new FormControl('', [
        Validators.pattern('^\\+?[0-9]+([-/ ]?[0-9]+)*$')]),
      'email': new FormControl('', [
        this.customEmailValidator]),
      'street1': new FormControl('', [
        Validators.required]),
      'street2': new FormControl(''),
      'postal_code': new FormControl('', [
        Validators.pattern('^[0-9]{3} ?[0-9]{2}$'),
        Validators.required]),
      'city': new FormControl('', [
        Validators.required]),
      'account_type': new FormControl('', [
        Validators.pattern('sv|iban|nul'),
        Validators.required]),
      'account_number': new FormControl(''),
      'clearing_code': new FormControl(''),
      'iban_number': new FormControl(''),
      'bic_code': new FormControl(''),
      'message_text': new FormControl('', [
        Validators.maxLength(6000)])
    });

    this.getUserInfo();
  }

  private getUserInfo(): void {

    this.isLoaded = false;

    const doLoad = (): void => {
      setTimeout(() => {
        if (!this.isLoaded) {
          this.spinner.show();
        }
      }, 500);

      this.auth.getUserInfo(this.sessionService.activeInstance.id).subscribe(
        (response: Arbetstagare) => {
          this.setUserInfo(response);
          this.spinner.hide();
        },
        (error) => {
          this.spinner.hide();
        });
    };

    if (!this.sessionService.hasSession) {
      this.router.navigate(['/login']);
      return;
    }

    if (!this.sessionService.hasActiveInstance) {
      this.sessionService.selectInstance(doLoad);
      return;
    }

    doLoad();
  }

  private setUserInfo(info: Arbetstagare): void {
    this.personal_number.setValue(info.personNr12Siffror);
    this.first_name.setValue(info.fornamn);
    this.last_name.setValue(info.efternamn);
    this.phone.setValue(info.telefon);
    this.email.setValue(info.epost);
    this.street1.setValue(info.gatuadress);
    this.street2.setValue(info.careof);
    this.postal_code.setValue(info.postnummer);
    this.city.setValue(info.postort);
    this.account_type.setValue(info.bankkontoTyp?.typ);
    this.account_number.setValue(info.kontonummer);
    this.clearing_code.setValue(info.clearing);
    this.iban_number.setValue(info.iban);
    this.bic_code.setValue(info.bic);
    this.message_text.setValue(info.meddelandeText);
    this.updateRequiredFields();
    this.isLoaded = true;
  }

  public updateRequiredFields(): void {
    this.userForm.controls.account_number.setValidators(this.account_type.value === 'sv' ? [Validators.pattern('^[0-9]+([-/ ]?[0-9]+)*$'), Validators.required] : null);
    this.userForm.controls.clearing_code.setValidators(this.account_type.value === 'sv' ? [Validators.pattern('^[1-9][0-9]{3}([- ]?[0-9])?$'), Validators.required] : null);
    this.userForm.controls.iban_number.setValidators(this.account_type.value === 'iban' ? [Validators.pattern('^[0-9a-zA-Z]+$'), Validators.required] : null);
    this.userForm.controls.bic_code.setValidators(this.account_type.value === 'iban' ? [Validators.pattern('^[0-9a-zA-Z]+$'), Validators.required] : null);
    this.userForm.controls.account_number.updateValueAndValidity();
    this.userForm.controls.clearing_code.updateValueAndValidity();
    this.userForm.controls.iban_number.updateValueAndValidity();
    this.userForm.controls.bic_code.updateValueAndValidity();
  }

  public openModal(content, windowClass?: NgbModalOptions): void {
    this.closeModal(this.lockedWindow);
    this.lockedWindow = this.modalService.open(content, windowClass);
  }

  public updateUser(): void {

    this.spinner.show();

    const params: Arbetstagare =
    {
      fornamn: this.first_name.value,
      efternamn: this.last_name.value,
      telefon: this.phone.value,
      epost: this.email.value,
      gatuadress: this.street1.value,
      careof: this.street2.value,
      postnummer: this.postal_code.value,
      postort: this.city.value,
      bankkontoTyp: this.account_type.value,
      kontonummer: this.account_number.value,
      clearing: this.clearing_code.value,
      iban: this.iban_number.value,
      bic: this.bic_code.value,
      meddelandeText: this.message_text.value
    };

    this.auth.updateUserInfo(this.sessionService.activeInstance.id, params).subscribe(
      (arbetstagare: Arbetstagare) => {
        this.setUserInfo(arbetstagare);
        this.userForm.markAsPristine();
        this.showModal('Dina uppgifter sparades.');
        this.spinner.hide();
      },
      (error) => {
        this.spinner.hide();
      });
  }

  private showModal(message: string): void {
    this.openModal(NgbdMessageModal, { centered: true });
    this.lockedWindow.componentInstance.icon = 'check';
    this.lockedWindow.componentInstance.title = this.instanceName;
    this.lockedWindow.componentInstance.message = message;
  }

  private closeModal(modalRef: NgbModalRef): void {
    if (!!modalRef) {
      modalRef.close();
    }
  }

  public get instanceName(): string {
    return this.sessionService.activeInstance.name;
  }
}
